import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProgressSpinner } from "primereact/progressspinner";

import PageHeader from "../../components/PageHeader/PageHeader";
import Button from "../../components/Button/Button";
import KpiCard from "../../components/KpiCard/KpiCard";
import { Tag } from "../../components/Tag/Tag";

import { useToast } from "../../context/ToastContext";
import { getColorBaseTokenValue } from "../../design-tokens";

import ProductionApi, { PreparationTimeResponse } from "../../services/ProductionApi";
import useLiveRefresh from "../../hooks/useLiveRefresh";
import { useShiftList } from "../../hooks/useWorkSchedule";
import ShiftFilter from "../../components/ShiftFilter/ShiftFilter";
import PeriodFilter from "../../components/PeriodFilter/PeriodFilter";
import { useActivityTracking } from "../../hooks/useActivityTracking";
import { ActivityEventType } from "../../utilities/activityLog";
import {
  describeSelection,
  DEFAULT_PERIOD_SELECTION,
  PeriodSelection,
  selectionDates,
  selectionReachesToday,
  selectionYmd,
} from "../../utilities/reportRange";
import PreparationChart from "./PreparationChart";
import styles from "./ReportPreparationTime.module.css";

const parseYmd = (s: string): Date => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const ReportPreparationTime: React.FC = () => {
  const { t, i18n } = useTranslation("preparationTime");
  const { displayToast } = useToast();
  // Log de atividade: emitido SÓ nos handlers abaixo. Instrumentar dentro de load()
  // geraria evento falso a cada push do SignalR (useLiveRefresh).
  const track = useActivityTracking();
  const locale = i18n.language?.toLowerCase().startsWith("en") ? "en-US" : "pt-BR";

  const [data, setData] = useState<PreparationTimeResponse | null>(null);
  const [loading, setLoading] = useState(true); // já há um load pendente no mount — evita flash do estado vazio
  const [lastUpdated, setLastUpdated] = useState("");
  const [activePeriod, setActivePeriod] = useState<PeriodSelection>(DEFAULT_PERIOD_SELECTION);
  // `null` = "Personalizado" escolhido sem as duas datas ainda → "Aplicar" desabilitado.
  const [pendingPeriod, setPendingPeriod] = useState<PeriodSelection | null>(
    DEFAULT_PERIOD_SELECTION,
  );

  const shifts = useShiftList();
  const [shiftId, setShiftId] = useState<string | null>(null);

  const fmt = useCallback(
    (v: number, dec = 1): string =>
      Number(v).toLocaleString(locale, { minimumFractionDigits: dec, maximumFractionDigits: dec }),
    [locale],
  );
  const fmtDateFull = useCallback(
    (d: Date): string => d.toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" }),
    [locale],
  );
  const fmtDateShort = useCallback(
    (d: Date): string => d.toLocaleDateString(locale, { day: "2-digit", month: "short" }),
    [locale],
  );
  const fmtAxis = useCallback(
    (d: Date): string => d.toLocaleDateString(locale, { day: "2-digit", month: "2-digit" }),
    [locale],
  );

  /**
   * Janela {from,to} da seleção. Preset de 30 dias → `null`, para o backend
   * aplicar a janela rolante dele (é o padrão da tela); presets curtos e
   * intervalo customizado → intervalo explícito.
   */
  const resolveRange = useCallback(
    (sel: PeriodSelection): { from: string; to: string } | null =>
      sel.kind === "preset" && sel.preset === "30" ? null : selectionYmd(sel),
    [],
  );

  const load = useCallback(
    async (sel: PeriodSelection, sid: string | null): Promise<void> => {
      setLoading(true);
      try {
        const range = resolveRange(sel);
        const params = { ...(range ?? {}), ...(sid ? { shiftId: sid } : {}) };
        const resp = await ProductionApi.GetPreparationTime(Object.keys(params).length ? params : undefined);
        setData((resp?.data as PreparationTimeResponse) ?? null);
        setActivePeriod(sel);
        setLastUpdated(new Date().toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" }));
      } catch (err: any) {
        displayToast("error", t("loadErrorTitle"), err?.errors?.[0] ?? t("loadErrorMsg"), false, 6000);
      } finally {
        setLoading(false);
      }
    },
    [resolveRange, displayToast, t, locale],
  );

  useEffect(() => {
    void load(DEFAULT_PERIOD_SELECTION, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rangeLabel = useMemo(() => {
    if (data?.from && data?.to) return `${fmtDateFull(parseYmd(data.from))} – ${fmtDateFull(parseYmd(data.to))}`;
    // Sem eco do backend, rotula pela janela pedida (inclui o intervalo customizado).
    const { from, to } = selectionDates(activePeriod);
    return `${fmtDateFull(from)} – ${fmtDateFull(to)}`;
  }, [data, activePeriod, fmtDateFull]);

  const derived = useMemo(() => {
    const series = data?.series ?? [];
    const stdSec = data?.standardCycleSeconds ?? 0;
    const stdMin = stdSec / 60;

    const rows = series.map((p) => ({
      date: parseYmd(p.date),
      avgMin: p.avgCycleSeconds == null ? null : p.avgCycleSeconds / 60,
      totalSec: p.avgCycleSeconds == null ? 0 : p.avgCycleSeconds * p.count,
      count: p.count,
    }));

    const withData = rows.filter((r) => r.avgMin != null);
    const hasData = !!data?.hasData && withData.length > 0;

    // Média do período: média ponderada por ciclo (Σ segundos ÷ Σ ciclos) — o tempo médio real de todos os ciclos.
    const totalSec = withData.reduce((a, r) => a + r.totalSec, 0);
    const totalCount = withData.reduce((a, r) => a + r.count, 0);
    const periodAvgMin = totalCount > 0 ? totalSec / totalCount / 60 : 0;
    const deltaMin = periodAvgMin - stdMin;

    // Melhor (mais rápido) / pior (mais lento) dia.
    let best = withData[0] ?? null;
    let worst = withData[0] ?? null;
    withData.forEach((r) => {
      if ((r.avgMin as number) < (best!.avgMin as number)) best = r;
      if ((r.avgMin as number) > (worst!.avgMin as number)) worst = r;
    });

    // Dias acima do padrão.
    const daysAbove = withData.filter((r) => (r.avgMin as number) > stdMin).length;
    const pctAbove = withData.length > 0 ? (daysAbove / withData.length) * 100 : 0;

    return {
      rows,
      stdMin,
      hasData,
      periodAvgMin,
      deltaMin,
      best,
      worst,
      daysAbove,
      daysWithData: withData.length,
      pctAbove,
    };
  }, [data]);

  // Atualização em tempo real via SignalR (substitui o botão "Atualizar").
  // Uma janela puramente histórica não muda — não vale reconsultar a cada evento
  // da estação (mesma decisão da Linha do Tempo no modo histórico).
  useLiveRefresh(
    () => void load(activePeriod, shiftId),
    selectionReachesToday(activePeriod),
  );

  const handleApply = (): void => {
    if (!pendingPeriod) return; // intervalo customizado incompleto
    track(ActivityEventType.FilterApply, describeSelection(pendingPeriod));
    void load(pendingPeriod, shiftId);
  };
  const handleReset = (): void => {
    track(ActivityEventType.FilterReset);
    setPendingPeriod(DEFAULT_PERIOD_SELECTION);
    setShiftId(null);
    void load(DEFAULT_PERIOD_SELECTION, null);
  };
  // O turno recarrega na hora (sem "Aplicar"), então carrega com a janela ATIVA.
  const handleShiftChange = (id: string | null): void => {
    track(ActivityEventType.ShiftChange, { shiftId: id });
    setShiftId(id);
    void load(activePeriod, id);
  };

  const colorGood = getColorBaseTokenValue("color.feedback.success.500");
  const colorBad = getColorBaseTokenValue("color.feedback.danger.500");

  const chartColors = useMemo(
    () => ({
      bars: derived.rows.map((r) =>
        r.avgMin == null ? "rgba(0,0,0,0)" : (r.avgMin as number) <= derived.stdMin ? colorGood : colorBad,
      ),
      ref: getColorBaseTokenValue("color.neutral.500"),
      text: getColorBaseTokenValue("color.neutral.500"),
      grid: getColorBaseTokenValue("color.neutral.100"),
    }),
    [derived.rows, derived.stdMin, colorGood, colorBad],
  );

  const signed = (v: number, dec = 1): string => `${v > 0 ? "+" : ""}${fmt(v, dec)}`;

  return (
    <div className={styles.page}>
      <PageHeader title={t("title")} description={t("subtitle")} className={styles.pageHeaderFlush}>
        <Tag
          label={lastUpdated ? `${t("updatedAt")} ${lastUpdated}` : t("loadingLabel")}
          severity="successInverse"
          icon="sync"
        />
      </PageHeader>

      {/* Filtros */}
      <div className={`${styles.card} ${styles.filters}`}>
        <PeriodFilter
          value={pendingPeriod}
          onChange={setPendingPeriod}
          rangeLabel={rangeLabel}
          label={t("fPeriod")}
          presetLabels={{ "7": t("preset7"), "14": t("preset14"), "30": t("preset30") }}
        />
        <ShiftFilter value={shiftId} onChange={handleShiftChange} shifts={shifts} />
        <div className={styles.filterSpacer} />
        <div className={styles.applyWrap}>
          <Button
            label={t("apply")}
            icon="check"
            severity="secondary"
            onClick={handleApply}
            loading={loading}
            disabled={!pendingPeriod}
          />
        </div>
      </div>

      {loading && !data ? (
        <div className={`${styles.card} ${styles.loading}`}>
          <ProgressSpinner style={{ width: 50, height: 50 }} strokeWidth="6" />
        </div>
      ) : derived.hasData ? (
        <>
          {/* KPIs */}
          <div className={styles.kpiRow}>
            <KpiCard
              title={t("kAvg")}
              value={`${fmt(derived.periodAvgMin)} ${t("unitMin")}`}
              icon="timer"
              variant="primary"
              descriptionIcon={derived.deltaMin > 0 ? "trending_up" : "trending_down"}
              trend={derived.deltaMin > 0 ? "down" : "up"}
              description={`${signed(derived.deltaMin)} ${t("unitMin")} ${t("vsStandard")}`}
            />
            <KpiCard
              title={t("kBestWorst")}
              value={derived.best ? `${fmt(derived.best.avgMin as number)} ${t("unitMin")}` : "—"}
              icon="swap_vert"
              variant="info"
              descriptionIcon="arrow_downward"
              trend="neutral"
              description={
                derived.best && derived.worst
                  ? `${fmtDateShort(derived.best.date)} · ${t("worst")} ${fmt(derived.worst.avgMin as number)} ${t("unitMin")} ${fmtDateShort(derived.worst.date)}`
                  : "—"
              }
            />
            <KpiCard
              title={t("kDaysAbove")}
              value={`${fmt(derived.pctAbove, 0)}%`}
              icon="warning"
              variant={derived.daysAbove > 0 ? "warning" : "success"}
              descriptionIcon={derived.daysAbove > 0 ? "priority_high" : "check"}
              trend={derived.daysAbove > 0 ? "warning" : "up"}
              description={`${derived.daysAbove} ${t("ofN")} ${derived.daysWithData} ${t("daysWord")} · ${t("aboveStd")} ${fmt(derived.stdMin)} ${t("unitMin")}`}
            />
          </div>

          {/* Gráfico */}
          <section className={styles.card}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionTitleWrap}>
                <span className={styles.sectionTitle}>
                  <span className="material-symbols-outlined">bar_chart</span>
                  {t("chartTitle")}
                </span>
                <span className={styles.sectionSub}>
                  {t("standardWord")} {fmt(derived.stdMin)} {t("unitMin")}
                </span>
              </div>
            </div>

            <div className={styles.chartBody}>
              <PreparationChart
                labels={derived.rows.map((r) => fmtAxis(r.date))}
                values={derived.rows.map((r) => r.avgMin)}
                counts={derived.rows.map((r) => r.count)}
                standard={derived.stdMin}
                colors={chartColors}
                avgLabel={t("tAvg")}
                countLabel={t("tCount")}
                diffLabel={t("tDiff")}
                standardLabel={t("legStandardShort")}
                unitMin={t("unitMin")}
                locale={locale}
              />
            </div>

            <div className={styles.legend}>
              <span className={styles.legendItem}>
                <span className={styles.legendSwatch} style={{ background: colorGood }} />
                {t("legBelow")}
              </span>
              <span className={styles.legendItem}>
                <span className={styles.legendSwatch} style={{ background: colorBad }} />
                {t("legAbove")}
              </span>
              <span className={styles.legendItem}>
                <span className={styles.legendLine} />
                {t("legStandardShort")} {fmt(derived.stdMin)} {t("unitMin")}
              </span>
            </div>
          </section>
        </>
      ) : (
        <div className={styles.card}>
          <div className={styles.empty}>
            <span className={`material-symbols-outlined ${styles.emptyIcon}`}>timer_off</span>
            <div>
              <div className={styles.emptyTitle}>{t("emptyTitle")}</div>
              <div className={styles.emptySub}>{t("emptySub")}</div>
            </div>
            <Button label={t("clearFilters")} icon="restart_alt" severity="neutral" variant="outlined" onClick={handleReset} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportPreparationTime;
